import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import CustomTextField from "../../../../components/customFields/CustomTextField";
import { editGoalApi } from "../../Api/DashboardApi";
import CustomSnackbar from "../../../../../newTestPreo/components/snackbar/Snackbar";
import CustomMessage from "../../../../components/CustomMessages/CustomMessage";
import DisableBlueSmallButton from "../../../../components/customButtons/DisableSmallBlueButton";

export default function EditGoal({
  selectedTestType,
  handleClose,
  handleOpenSuccessPopup,
  setSelectedTestType,
}) {
  const [scores, setScores] = useState({});
  const [submitted, setSubmitted] = useState(false); // ✅ NEW STATE
  const userId = localStorage.getItem("userId");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const initialScores = {};

    selectedTestType?.module_list.forEach((item) => {
      initialScores[item.abbreviation] = item.value;
    });

    if (selectedTestType?.tentative_test_date) {
      const date = new Date(selectedTestType.tentative_test_date);
      const formattedDate = date.toISOString().split("T")[0];
      initialScores["tentative_test_date"] = formattedDate;
    }

    setScores(initialScores);
  }, [selectedTestType]);

  const handleScoreChange = (field, value) => {
    setScores((prev) => ({
      ...prev,
      [field]: field === "tentative_test_date" ? value : parseInt(value),
    }));
  };

  const handleSubmitData = async () => {
    setSubmitted(true); // ✅ enable error display

    try {
      const invalidDate = scores?.tentative_test_date;

      const invalidModules = selectedTestType?.module_list?.filter((module) => {
        const value = Number(scores[module.abbreviation]);
        return (
          value < module.min_score || value > module.max_score || isNaN(value)
        );
      });

      if (!invalidDate || invalidModules.length > 0) {
        // ❌ Don’t submit if validation fails
        return;
      }

      const payload = {
        user_id: parseInt(userId),
        test_type_id: selectedTestType?.id,
        ...scores,
      };

      const response = await editGoalApi(payload);

      if (response.status === 200) {
        setSelectedTestType((prev) => ({
          ...prev,
          tentative_test_date: scores?.tentative_test_date,
          module_list: prev.module_list.map((m) => ({
            ...m,
            value: scores[m.abbreviation],
          })),
        }));

        handleClose();
        handleOpenSuccessPopup();
      } else {
        showSnackbar(response.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Submission error:", error);
      showSnackbar("An unexpected error occurred.", "error");
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {/* Tentative Date */}
            <Grid item xs={12}>
              <Typography color="#333333" fontFamily="Inter" fontSize="14px" mb={1}>
                Tentative Exam Date
              </Typography>
              <CustomTextField
                value={scores?.tentative_test_date || ""}
                onChange={(e) =>
                  handleScoreChange("tentative_test_date", e.target.value)
                }
                type="date"
                size="small"
                fullWidth
                color="#9D0385"
                fontWeight={700}
                error={submitted && !scores?.tentative_test_date} // ✅ only show error after submit
                helperText={
                  submitted && !scores?.tentative_test_date
                    ? "This field is required"
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                border="1px solid #ccc"
                borderRadius="8px"
                padding="8px 12px"
                justifyContent="space-between"
              >
                <Typography
                  fontWeight={400}
                  color="#9D0385"
                  fontFamily="Inter"
                  fontSize="16px"
                >
                  {selectedTestType?.name}
                </Typography>
              </Box>
            </Grid>

            {/* Modules */}
            {selectedTestType?.module_list?.map((module) => {
              const field = module.abbreviation;
              const value = scores[field] || "";
              const isInvalid =
                value !== "" &&
                (Number(value) < module.min_score ||
                  Number(value) > module.max_score);
              const isRequiredInvalid = value === "";
              const showError = submitted && (isInvalid || isRequiredInvalid); // ✅ only show after submit

              return (
                <Grid item xs={12} key={module.module_id}>
                  <Typography
                    color="#333333"
                    fontFamily="Inter"
                    fontSize="14px"
                    mb={1}
                  >
                    {module.module_name}
                  </Typography>
                  <CustomTextField
                    type="number"
                    value={scores[field] || ""}
                    onChange={(e) => handleScoreChange(field, e.target.value)}
                    placeholder={`Enter score (${module.min_score}–${module.max_score})`}
                    size="small"
                    fullWidth
                    inputProps={{
                      min: module.min_score,
                      max: module.max_score,
                      required: true,
                    }}
                    error={showError}
                    helperText={
                      !submitted
                        ? ""
                        : isRequiredInvalid
                        ? "This field is required"
                        : isInvalid
                        ? `Score must be between ${module.min_score} and ${module.max_score}`
                        : ""
                    }
                  />
                </Grid>
              );
            })}

            {selectedTestType?.module_list?.length === 0 && (
              <Grid item xs={12}>
                <CustomMessage message="Goals can’t be set since the test has no measurable scores." />
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Buttons */}
        {selectedTestType?.module_list?.length > 0 && (
          <Grid item xs={12} container gap={2} justifyContent="center">
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmitData}
              style={{
                background: "#572575",
                color: "#FFFFFF",
                fontFamily: "Inter",
                fontWeight: 500,
                paddingLeft: "20px",
                paddingRight: "20px",
                fontSize: "14px",
              }}
            >
              Save Changes
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={handleClose}
              style={{
                borderColor: "#6A1B9A",
                color: "#572575",
                fontFamily: "Inter",
                fontWeight: 700,
                paddingLeft: "20px",
                paddingRight: "20px",
                fontSize: "14px",
              }}
            >
              Cancel
            </Button>
          </Grid>
        )}

        {selectedTestType?.module_list?.length === 0 && (
          <Grid item xs={12} container gap={2} justifyContent="center">
            <DisableBlueSmallButton buttonTitle="Save Changes" width="27%" />
            <Button
              size="small"
              variant="outlined"
              onClick={handleClose}
              style={{
                borderColor: "#6A1B9A",
                color: "#572575",
                fontFamily: "Inter",
                fontWeight: 700,
                paddingLeft: "20px",
                paddingRight: "20px",
                fontSize: "14px",
              }}
            >
              Cancel
            </Button>
          </Grid>
        )}
      </Grid>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}
